import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { Product, TransactionPurchase } from '../../../../helpers/models/Models';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import { formatMoney } from '../../../../helpers/DateFormat';
import DialogBox from '../../../../constants/DialogBox';
import { useFormik } from 'formik';
import { putDataToTheServer } from '../../../../helpers/api';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { PurchaseStatusOptions } from '../../../../constants/Options';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';

interface ProductListViewProps {
    data: {
        results: TransactionPurchase[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}
const PurhcaseReportListView: React.FC<ProductListViewProps> = ({ data, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<TransactionPurchase | null>(null);

    const columns = [
        { header: 'Product Name', accessor: 'product_name', sortable: true },
        { header: 'TSN', accessor: 'tsn', sortable: false },
        { header: 'Purchase QTY', accessor: 'purchase_qty', sortable: false },
        { header: 'Purchase Rate', accessor: 'purchase_rate', sortable: false },
        { header: 'Purchase Total Amt', accessor: 'pur_tot_amt', sortable: false },
        { header: 'Purchase Status', accessor: 'pur_status', sortable: true },
        { header: 'Store Name', accessor: 'store_name', sortable: false },
        { header: 'Store Phone No.', accessor: 'phone_no', sortable: false },
        { header: 'Suppiler Name', accessor: 'suppiler_name', sortable: false },
        { header: 'Suppiler Phone No.', accessor: 'sup_phone', sortable: false },
        { header: 'Purchase Date', accessor: 'pur_sale_date', sortable: false },
        { header: 'Discount Amt', accessor: 'dis_amt', sortable: false },
        { header: 'Discount Percentage', accessor: 'dis_per', sortable: false },
        { header: 'With VAT', accessor: 'vat_amt', sortable: false },
        { header: 'Actions', accessor: 'actions', action: true },
    ];

    const modifiedData = useMemo(() => {
        if (!data) return [];
        return data.results.map((item: TransactionPurchase) => ({
            sn: item.sn,
            tsn: item.tsn,
            sale_issue: item.sale_issue,
            purchase_qty: `${item.purchase_qty} ${item.purchase_unit_name}`,
            pur_tot_amt: item.pur_tot_amt,
            dis_amt: item.dis_amt,
            dis_per: item.dis_per,
            vat_amt: item.vat_amt,
            purchase_rate: item.purchase_rate,
            sup_phone: item.sup_phone,
            pur_sale_date: item.pur_sale_date,
            purchase_unit_name: item.purchase_unit_name,
            store_name: item.store_name,
            phone_no: item.phone_no,
            suppiler_name: item.suppiler_name,
            product_name: item.product_name,
            remarks: item.remarks,
            active: item.purchase_status === 2 ? 1 : item.purchase_status === 1 ? 0 : 2,
            purchase_status: item.purchase_status, // keep this as a number
            pur_status: item.purchase_status === 1 ? "Ordered" : item.purchase_status === 2 ? "Received" : "Pending", // string type
        }));
    }, [data]);

    const handleEdit = (item: TransactionPurchase) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };
    const formik = useFormik({
        initialValues: {
            product_name: currentItem?.product_name || "",
            suppiler_name: currentItem?.suppiler_name || "",
            remarks: currentItem?.remarks || "",
            tsn: currentItem?.tsn || "",
            pur_sale_date: currentItem?.pur_sale_date || "",
            purchase_status: currentItem?.purchase_status || "",
            purchase_rate: currentItem?.purchase_rate || "",
            pur_tot_amt: currentItem?.pur_tot_amt ||"",
            purchase_qty: currentItem?.purchase_qty ||"",

        },
        // validationSchema: readerEditValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("purchase_status", values.purchase_status?.toString());
            formData.append("remarks", values.remarks);
            try {
                await putDataToTheServer(AppURL.InventoryURL + 'inventory/product/transaction/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    }
                    else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });
    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onNext={() => data?.next && setOnURL(data.next)}
                onEdit={handleEdit}
                onPrev={() => data?.previous && setOnURL(data.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Product Purchase Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='Date(BS)'
                                    label='Date'
                                    handleChange={formik.handleChange}
                                    value={formik.values.pur_sale_date}
                                    error={typeof formik.errors.pur_sale_date === 'string' ? formik.errors.pur_sale_date : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.pur_sale_date}
                                    required={true}
                                    disabled
                                    placeholder='Enter Date(BS)'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='product_name'
                                    label='Product Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.product_name}
                                    error={typeof formik.errors.product_name === 'string' ? formik.errors.product_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.product_name}
                                    required={true}
                                    disabled
                                    placeholder='Enter Product Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='purchase_qty'
                                    label='Purchase QTY'
                                    handleChange={formik.handleChange}
                                    value={formik.values.purchase_qty}
                                    error={typeof formik.errors.purchase_qty === 'string' ? formik.errors.purchase_qty : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.purchase_qty}
                                    required={true}
                                    disabled
                                    placeholder='Enter Product Purchase QTY'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='pur_tot_amt'
                                    label='Purchase Total Amt'
                                    handleChange={formik.handleChange}
                                    value={formik.values.pur_tot_amt}
                                    error={typeof formik.errors.pur_tot_amt === 'string' ? formik.errors.pur_tot_amt : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.pur_tot_amt}
                                    required={true}
                                    disabled
                                    placeholder='Enter Purchase Total Amt'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='suppiler_name'
                                    label='Suppiler Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.suppiler_name}
                                    error={typeof formik.errors.suppiler_name === 'string' ? formik.errors.suppiler_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.suppiler_name}
                                    required={true}
                                    disabled
                                    placeholder='Enter Suppiler Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="purchase_status"
                                    label='Purchase Status'
                                    disabled={false} // Adjust based on your needs
                                    options={PurchaseStatusOptions}
                                    value={formik.values.purchase_status}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    required
                                    placeholder="Select Purchase Status"
                                />
                            </Grid>
                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='remarks'
                                    label='Remarks'
                                    handleChange={formik.handleChange}
                                    value={formik.values.remarks}
                                    error={typeof formik.errors.remarks === 'string' ? formik.errors.remarks : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.remarks}
                                    required={false}
                                    placeholder='Enter Remarks'
                                />
                            </Grid>
                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    )
}

export default PurhcaseReportListView